import { ArrowLeft, ShieldAlert, ShieldCheck, UserPlus } from "lucide-react";
import { useEffect, useRef, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import type { AccountView, InvitationOutcome } from "../../../shared/contracts.js";
import { isPartsRole, type Role } from "../../../shared/domain.js";
import { isClientApiError } from "../../api/http.js";
import {
  accountMutationErrorMessage,
  useAccountHistory,
  useAccounts,
  useDeactivateAccount,
  useResetPassword,
  useSetAdministration,
} from "../../api/accountQueries.js";
import { useSession } from "../../app/AppProviders.js";
import { roleHomePath } from "../../app/RoleHomeRedirect.js";
import { AsyncPanel } from "../../ui/AsyncPanel.js";
import {
  Button,
  buttonAttributes,
  ControlSizeProvider,
  useControlSize,
  type ControlSize,
} from "../../ui/Button.js";
import { ConfirmDialog } from "../../ui/Dialog.js";
import { formatInstant } from "../cases/detail/ActivityTimeline.js";
import { roleLabel } from "../cases/parts-actions/RecipientWarning.js";
import { temporaryPasswordError } from "../auth/password-rules.js";
import { actorLabel, describeAccountEvent } from "./account-history.js";
import {
  AccountDialog,
  TemporaryPasswordField,
  type AccountDialogMode,
} from "./AccountDialog.js";
import { TemporaryPasswordDialog } from "./TemporaryPasswordDialog.js";

/**
 * `/accounts` — employee account administration (spec §2 "Account
 * administration"): create an account with a temporary password that is
 * emailed to the person (and shown here once), edit the name and role,
 * deactivate, reset a password, and grant or revoke the administration
 * capability itself (the Access column's switch). While an active account's
 * temporary password is still to be replaced, the Status column says until
 * when it works, or that it has expired. A creation and a reset each offer
 * an optional temporary password the administrator types instead of having
 * one generated (owner decision 2026-09-17: the dealership's mail server
 * purges the invitation emails, so the administrator hands the password
 * over from their own work email for now); the reset's field lives in the
 * confirmation, checked in place with the change-password page's rules.
 *
 * Only a session with the administration capability sees the list; anyone
 * else gets a plain refusal and a link home. Administration is separate from
 * the operational role, so the page changes nothing about what a person can
 * do with orders, and the server checks `isAdmin` on every route regardless
 * of what this page renders. Every destructive or history-changing action —
 * deactivation, password reset, removing administration — sits behind an
 * explicit confirmation; granting administration applies straight from the
 * switch. The server refuses to strip the last active administrator (409
 * `LAST_ADMINISTRATOR`), and the confirmation shows that refusal in place.
 *
 * Below the table, "Recent changes" is the account-administration history:
 * one sentence per change, who made it (an administrator, or the
 * deployment's bootstrap) and when, newest first. It is read from the
 * server and refreshed with the table after every mutation; the page never
 * composes an entry itself.
 *
 * The temporary password a create or reset returns is held in this
 * component's state for exactly as long as {@link TemporaryPasswordDialog}
 * is open, then cleared. It never enters a query, the URL or storage. The
 * one the administrator types for a reset is held the same way, for as
 * long as the reset confirmation is open, and reaches the request through
 * the hook's accessor rather than as a mutation variable, so the mutation
 * cache never holds it either.
 *
 * A dialog remembers *which* account it is about, not what the account looked
 * like when it opened: every render resolves that id against the list the
 * query currently holds. So when a save is refused as stale (409) and the
 * list reloads, the open dialog carries the account's fresh version and
 * "check it and try again" can succeed without closing and reopening.
 *
 * Controls follow the administrator's device (spec §3): Parts works at a
 * desktop (28 px minimum), every other role on the Surface Pro (44 px).
 * Administration is a capability on top of the role, so the role decides.
 */

type Confirmation =
  | Readonly<{ kind: "deactivate"; account: AccountView }>
  | Readonly<{ kind: "reset"; account: AccountView }>
  | Readonly<{ kind: "revoke_admin"; account: AccountView }>;

type Reveal = Readonly<{
  password: string;
  invitation: InvitationOutcome;
  accountName: string;
  reason: "created" | "reset";
}>;

/** The control size for a role's primary device (spec §3): the Parts roles work at a desktop. */
function controlSizeForRole(role: Role): ControlSize {
  return isPartsRole(role) ? "desktop" : "touch";
}

export function AccountsPage() {
  const { user } = useSession();
  const isAdmin = user?.isAdmin === true;
  const homePath = user ? roleHomePath(user.role) : "/";
  const controlSize: ControlSize = user
    ? controlSizeForRole(user.role)
    : "desktop";

  const accounts = useAccounts({ enabled: isAdmin });
  const history = useAccountHistory({ enabled: isAdmin });

  const [dialog, setDialog] = useState<AccountDialogMode | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [reveal, setReveal] = useState<Reveal | null>(null);
  const [status, setStatus] = useState("");
  /** A refused grant, shown beside the table (revocations report inside their confirmation). */
  const [grantError, setGrantError] = useState<string | null>(null);

  /**
   * The control that started a flow ending in the password reveal (New
   * account, Reset password). The reveal opens from inside another dialog,
   * so the dialogs' own focus restoration cannot reach the original opener;
   * the page hands focus back once the reveal closes.
   */
  const flowOpenerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (reveal !== null) return;
    const opener = flowOpenerRef.current;
    flowOpenerRef.current = null;
    if (opener && document.contains(opener)) opener.focus();
  }, [reveal]);

  const deactivate = useDeactivateAccount();
  const administration = useSetAdministration();
  /** The account whose reset is in flight, for naming the reveal. */
  const resettingRef = useRef<AccountView | null>(null);
  /** The temporary password typed into the reset confirmation; blank means the server generates one. */
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const reset = useResetPassword({
    onTemporaryPassword: (password, invitation) => {
      const account = resettingRef.current;
      resettingRef.current = null;
      // The confirmation that asked for the reset closes and the reveal opens
      // in the same render, so no other dialog ever holds the value.
      setConfirmation(null);
      setResetPassword("");
      setReveal({
        password,
        invitation,
        accountName: account?.displayName ?? "the account",
        reason: "reset",
      });
    },
    // Read at send time from this state, never handed to `mutate`.
    chosenTemporaryPassword: () =>
      resetPassword.length > 0 ? resetPassword : undefined,
  });

  if (!user) return null;

  if (!isAdmin) {
    return (
      <main className="accounts-page accounts-page--denied">
        <section
          className="placeholder-card"
          aria-labelledby="accounts-denied-title"
        >
          <ShieldAlert
            className="accounts-denied__icon"
            aria-hidden="true"
            focusable="false"
          />
          <h1 id="accounts-denied-title" className="placeholder-card__title">
            Only administrators can manage accounts
          </h1>
          <p className="placeholder-card__lead">
            Your account can do everything its role allows, but creating
            accounts, assigning roles, deactivating and resetting passwords need
            the administration capability. Ask an administrator if you think
            this is a mistake.
          </p>
          <Link
            to={homePath}
            {...buttonAttributes({ variant: "primary", size: controlSize })}
          >
            Go to my home
          </Link>
        </section>
      </main>
    );
  }

  const rememberOpener = (event: MouseEvent<HTMLElement>) => {
    flowOpenerRef.current = event.currentTarget;
  };

  const closeConfirmation = () => {
    setConfirmation(null);
    // Whatever was typed for one account never carries to the next confirmation.
    setResetPassword("");
    setResetPasswordError(null);
    deactivate.reset();
    reset.reset();
    administration.reset();
  };

  /** The switch: a grant applies at once; a revocation asks first. */
  const toggleAdministration = (account: AccountView) => {
    if (account.isAdmin) {
      // One mutation serves grants and revocations, and the revoke
      // confirmation reports that mutation's error in place. A grant
      // refused a moment ago (its message sits beside the table) must not
      // greet the next confirmation as if it were about this account.
      administration.reset();
      setConfirmation({ kind: "revoke_admin", account });
      return;
    }
    setGrantError(null);
    administration.mutate(
      { userId: account.id, isAdmin: true, expectedVersion: account.version },
      {
        onSuccess: (updated) => {
          setStatus(`${updated.displayName} is now an administrator.`);
        },
        onError: (error) => {
          setGrantError(accountMutationErrorMessage(error));
        },
      },
    );
  };

  const confirmRevokeAdministration = (account: AccountView) => {
    administration.mutate(
      { userId: account.id, isAdmin: false, expectedVersion: account.version },
      {
        onSuccess: (updated) => {
          setStatus(`${updated.displayName} is no longer an administrator.`);
          setConfirmation(null);
        },
      },
    );
  };

  const confirmDeactivate = (account: AccountView) => {
    deactivate.mutate(
      { userId: account.id, expectedVersion: account.version },
      {
        onSuccess: (updated) => {
          setStatus(
            `${updated.displayName}'s account was deactivated and their sessions were ended.`,
          );
          setConfirmation(null);
        },
      },
    );
  };

  const confirmReset = (account: AccountView) => {
    // A typed password is judged here, in place, before anything is sent.
    const problem = temporaryPasswordError(resetPassword) ?? null;
    setResetPasswordError(problem);
    reset.reset();
    if (problem !== null) return;
    resettingRef.current = account;
    reset.mutate(
      { userId: account.id },
      {
        onSuccess: () => {
          setStatus(`${account.displayName}'s password was reset.`);
        },
      },
    );
  };

  const rows = accounts.data ?? [];
  const events = history.data ?? [];

  /**
   * The account a dialog is about, as the list holds it now; the snapshot
   * taken at the click only bridges the moment the list has no such row
   * (it never disappears — accounts are deactivated, not deleted).
   */
  const fresh = (snapshot: AccountView): AccountView =>
    rows.find((row) => row.id === snapshot.id) ?? snapshot;
  const dialogMode: AccountDialogMode | null =
    dialog?.kind === "edit"
      ? { kind: "edit", account: fresh(dialog.account) }
      : dialog;
  /** Read once per render: every row's deadline is judged against the same instant. */
  const now = Date.now();
  const confirming: AccountView | null = confirmation
    ? fresh(confirmation.account)
    : null;

  return (
    <ControlSizeProvider size={controlSize}>
      <main className="accounts-page">
        <header className="accounts-header">
          <div className="accounts-header__identity">
            <p className="auth-card__brand">Chicagoland Auto Tools</p>
            <h1 className="accounts-header__title">Accounts</h1>
            <p className="accounts-header__lead">
              Employee sign-ins for the parts tracker. New accounts and password
              resets email the person a temporary password, valid for 72 hours,
              that they replace at their first sign-in; it is shown to you once
              as well.
            </p>
          </div>
          <div className="accounts-header__actions">
            <Link
              to={homePath}
              {...buttonAttributes({ variant: "quiet", size: controlSize })}
            >
              <span className="button__icon" aria-hidden="true">
                <ArrowLeft />
              </span>
              <span className="button__label">Back to my workspace</span>
            </Link>
            <Button
              variant="primary"
              icon={<UserPlus />}
              onClick={(event) => {
                rememberOpener(event);
                setDialog({ kind: "create" });
              }}
            >
              New account
            </Button>
          </div>
        </header>

        <p role="status" aria-live="polite" className="accounts-status">
          {status}
        </p>
        {grantError !== null ? (
          <p role="alert" className="form-alert accounts-alert">
            {grantError}
          </p>
        ) : null}

        <AsyncPanel
          isLoading={accounts.isPending}
          error={accounts.error}
          isEmpty={accounts.isSuccess && rows.length === 0}
          onRetry={() => void accounts.refetch()}
          loadingLabel="Loading accounts…"
          emptyTitle="No accounts yet"
          emptyDescription="Create the first employee account with New account."
        >
          <section className="accounts-table-card">
            <table className="accounts-table" aria-label="Accounts">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Email</th>
                  <th scope="col">Role</th>
                  <th scope="col">Access</th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((account) => {
                  const isSelf = account.id === user.id;
                  return (
                    <tr
                      key={account.id}
                      className="accounts-table__row"
                      data-active={account.active ? "true" : "false"}
                    >
                      <th scope="row" className="accounts-table__name">
                        <span>{account.displayName}</span>
                        {isSelf ? (
                          <span className="accounts-table__self">(you)</span>
                        ) : null}
                      </th>
                      <td className="accounts-table__email" data-label="Email">
                        {account.email}
                      </td>
                      <td data-label="Role">{roleLabel(account.role)}</td>
                      <td data-label="Access">
                        <span className="accounts-table__access">
                          {account.active ? (
                            <AdministrationSwitch
                              account={account}
                              busy={
                                administration.isPending &&
                                administration.variables?.userId === account.id
                              }
                              onToggle={() => toggleAdministration(account)}
                            />
                          ) : null}
                          {account.isAdmin ? (
                            <span className="accounts-table__admin">
                              <ShieldCheck aria-hidden="true" focusable="false" />
                              Administrator
                            </span>
                          ) : (
                            <span className="accounts-table__muted">
                              Standard
                            </span>
                          )}
                        </span>
                      </td>
                      <td data-label="Status">
                        <span
                          className="accounts-table__state"
                          data-state={account.active ? "active" : "inactive"}
                        >
                          {account.active ? "Active" : "Deactivated"}
                        </span>
                        {account.active &&
                        account.temporaryPasswordExpiresAt !== null ? (
                          <TemporaryPasswordStatus
                            expiresAt={account.temporaryPasswordExpiresAt}
                            now={now}
                          />
                        ) : null}
                      </td>
                      <td className="accounts-table__actions">
                        {account.active ? (
                          <>
                            <Button
                              variant="quiet"
                              aria-label={`Edit ${account.displayName}`}
                              onClick={() =>
                                setDialog({ kind: "edit", account })
                              }
                            >
                              Edit
                            </Button>
                            <Button
                              variant="quiet"
                              aria-label={`Reset password for ${account.displayName}`}
                              onClick={(event) => {
                                rememberOpener(event);
                                setConfirmation({ kind: "reset", account });
                              }}
                            >
                              Reset password
                            </Button>
                            <Button
                              variant="quiet"
                              className="accounts-table__danger"
                              aria-label={`Deactivate ${account.displayName}`}
                              onClick={() =>
                                setConfirmation({ kind: "deactivate", account })
                              }
                            >
                              Deactivate
                            </Button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </AsyncPanel>

        <section
          className="accounts-history"
          aria-labelledby="accounts-history-title"
        >
          <h2 id="accounts-history-title" className="accounts-history__title">
            Recent changes
          </h2>
          <AsyncPanel
            isLoading={history.isPending}
            error={history.error}
            isEmpty={history.isSuccess && events.length === 0}
            onRetry={() => void history.refetch()}
            loadingLabel="Loading history…"
            headingLevel={3}
            emptyTitle="No changes recorded yet"
            emptyDescription="Every account created, renamed or given a role, every password reset, sign-in email sent or failed, deactivation and change of administration will be listed here, with who made it and when."
          >
            <ol className="accounts-history__list">
              {events.map((event) => (
                <li key={event.id} className="accounts-history__entry">
                  <p className="accounts-history__sentence">
                    {describeAccountEvent(event)}
                  </p>
                  <p className="accounts-history__meta">
                    by {actorLabel(event)} ·{" "}
                    <time dateTime={event.createdAt}>
                      {formatInstant(event.createdAt)}
                    </time>
                  </p>
                </li>
              ))}
            </ol>
          </AsyncPanel>
        </section>

        <AccountDialog
          mode={dialogMode}
          onClose={() => setDialog(null)}
          onTemporaryPassword={(password, invitation, created) =>
            setReveal({
              password,
              invitation,
              accountName: created.displayName,
              reason: "created",
            })
          }
          onSaved={setStatus}
        />

        <ConfirmDialog
          open={confirmation?.kind === "deactivate"}
          onClose={closeConfirmation}
          onConfirm={() => {
            if (confirmation?.kind === "deactivate" && confirming)
              confirmDeactivate(confirming);
          }}
          title={`Deactivate ${confirming?.displayName ?? "this account"}?`}
          description="They are signed out everywhere immediately and can no longer sign in. Their comments and history stay on record — accounts are never deleted."
          confirmLabel="Deactivate account"
          tone="destructive"
          pending={deactivate.isPending}
        >
          {deactivate.isError ? (
            <p role="alert" className="form-alert">
              {accountMutationErrorMessage(deactivate.error)}
            </p>
          ) : null}
        </ConfirmDialog>

        <ConfirmDialog
          open={confirmation?.kind === "revoke_admin"}
          onClose={closeConfirmation}
          onConfirm={() => {
            if (confirmation?.kind === "revoke_admin" && confirming)
              confirmRevokeAdministration(confirming);
          }}
          title={`Remove administration from ${confirming?.displayName ?? "this account"}?`}
          description={
            confirming?.id === user.id
              ? "You will no longer be able to open Accounts: create accounts, assign roles, reset passwords, deactivate accounts or change who administers. Your own role and sign-in are unchanged."
              : "They can no longer open Accounts: create accounts, assign roles, reset passwords, deactivate accounts or change who administers. Their role and sign-in are unchanged."
          }
          confirmLabel="Remove administration"
          tone="destructive"
          pending={administration.isPending}
        >
          {administration.isError ? (
            <p role="alert" className="form-alert">
              {accountMutationErrorMessage(administration.error)}
            </p>
          ) : null}
        </ConfirmDialog>

        <ConfirmDialog
          open={confirmation?.kind === "reset"}
          onClose={closeConfirmation}
          onConfirm={() => {
            if (confirmation?.kind === "reset" && confirming)
              confirmReset(confirming);
          }}
          title={`Reset ${confirming?.displayName ?? "this account"}'s password?`}
          description="They are signed out everywhere and get a new temporary password, emailed to them and shown to you once, valid for 72 hours and replaced at their next sign-in."
          confirmLabel="Reset password"
          pending={reset.isPending}
        >
          {reset.isError && !resetServerFieldError(reset.error) ? (
            <p role="alert" className="form-alert">
              {accountMutationErrorMessage(reset.error)}
            </p>
          ) : null}
          <TemporaryPasswordField
            id="reset-temporary-password"
            value={resetPassword}
            onChange={setResetPassword}
            error={resetPasswordError ?? resetServerFieldError(reset.error)}
          />
        </ConfirmDialog>

        <TemporaryPasswordDialog
          password={reveal?.password ?? null}
          invitation={reveal?.invitation ?? null}
          accountName={reveal?.accountName ?? ""}
          reason={reveal?.reason ?? "created"}
          onClose={() => setReveal(null)}
        />
      </main>
    </ControlSizeProvider>
  );
}

/** The server's own verdict on a typed temporary password (422), for the field rather than the alert. */
function resetServerFieldError(error: unknown): string | null {
  if (!isClientApiError(error) || error.status !== 422) return null;
  return error.fieldErrors["temporaryPassword"]?.[0] ?? null;
}

/**
 * The second line under Status while an active account still holds a
 * temporary password it has not replaced: until when it works (Chicago
 * time, the table's short form — the dialog and the email spell the deadline
 * out), or that it has expired and the administrator resets it. A
 * deactivated row never shows it: the deadline stays on the row the server
 * holds, but the password cannot sign in and the row offers no reset (the
 * server would refuse one), so "reset it" would be an instruction with no
 * control. Judged against the page's render instant, never a timer: the
 * next refetch or render is soon enough for a line that turns over once in
 * 72 hours.
 */
function TemporaryPasswordStatus({
  expiresAt,
  now,
}: {
  expiresAt: string;
  now: number;
}) {
  const expired = Date.parse(expiresAt) <= now;
  return (
    <span
      className="accounts-table__temporary"
      data-expired={expired ? "true" : "false"}
    >
      {expired ? (
        "Temporary password expired — reset it"
      ) : (
        <>
          Temporary password until{" "}
          <time dateTime={expiresAt}>{formatInstant(expiresAt)}</time>
        </>
      )}
    </span>
  );
}

/**
 * The Access column's switch: on = administrator. It is a real `switch`
 * (`aria-checked`), sized like every other control on the page, and its
 * accessible name carries the account so a screen reader hears which
 * person it governs. The visible Administrator / Standard label beside it
 * is what the eye reads.
 */
function AdministrationSwitch({
  account,
  busy,
  onToggle,
}: {
  account: AccountView;
  busy: boolean;
  onToggle: () => void;
}) {
  const size = useControlSize();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={account.isAdmin}
      aria-label={`Administrator access for ${account.displayName}`}
      className="admin-switch"
      data-size={size}
      data-checked={account.isAdmin ? "true" : "false"}
      disabled={busy}
      aria-busy={busy ? "true" : undefined}
      onClick={onToggle}
    >
      <span className="admin-switch__track" aria-hidden="true">
        <span className="admin-switch__knob" />
      </span>
    </button>
  );
}
